import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid';
import cx from 'classnames';
import { allSections } from 'components/sections';
import { widgetResolver } from 'utils/pageLoader';
import Authorization from 'containers/Authorization';
import Subsection from './Subsection';
import SectionDropZone from './SectionDropZone';
import style from './SectionLoader.scss';
import StoreContext from '../../../../../modules/react/components/stateful/StoreProvide';

export default class SectionLoader extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    position: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    data: PropTypes.object,
    payload: PropTypes.object,
    isEditing: PropTypes.bool,
    widgets: PropTypes.array.isRequired,
    isWidgetBeingEdited: PropTypes.func,
    onWidgetEditStart: PropTypes.func,
    onWidgetEditStop: PropTypes.func,
    onWidgetChange: PropTypes.func,
    onCanDrop: PropTypes.func,
    onDrop: PropTypes.func,
    onDelete: PropTypes.func,
    onSwapUp: PropTypes.func,
    onSwapDown: PropTypes.func,
    onEditStart: PropTypes.func,
    onFinishEdit: PropTypes.func,
    onCancelEdit: PropTypes.func,
    sectionWidgetMoving: PropTypes.bool.isRequired,
    onStartWidgetSwitch: PropTypes.func,
    onSwitchWidgets: PropTypes.func,
    facilityId: PropTypes.string,
    pageFacilityId: PropTypes.string,
    locale: PropTypes.string,
    isSubmitting: PropTypes.bool,
    legacyWidgetLoader: PropTypes.bool,
  };

  static contextType = StoreContext;

  static defaultProps = {
    isEditing: false,
    payload: {},
    data: {},
  };

  getSection() {
    return allSections[this.props.type] || allSections.Placeholder;
  }

  wrapHandler = fn => (...args) => this.props[fn](this.props.id, ...args);

  handleEditStart = this.wrapHandler('onEditStart');
  handleFinishEdit = this.wrapHandler('onFinishEdit');
  handleCancelEdit = this.wrapHandler('onCancelEdit');
  handleDrop = this.wrapHandler('onDrop');
  handleDelete = this.wrapHandler('onDelete');
  handleSwapUp = this.wrapHandler('onSwapUp');
  handleSwapDown = this.wrapHandler('onSwapDown');
  handleShowSwapArrows = this.wrapHandler('showSwapArrows');

  handleCanDrop = (...args) => this.props.onCanDrop(this.props.position, ...args);

  handleFinishEditSubsection = (widgetId, payload) => {
    const widgets = this.props.widgets.map((widget) => {
      if (widget && widget.id === widgetId) {
        const newWidget = { ...widget, payload: { ...(widget.payload || {}), ...payload } };
        // We'll run the widget dispatcher again in the event that this creates new async requirements
        widgetResolver(this.context, newWidget);
        return newWidget;
      }

      return widget;
    });

    this.props.onWidgetChange(this.props.id, widgets);
    // todo: Only stop editing after we've verified that the changes were Kosher
    this.props.onWidgetEditStop(widgetId);
  };

  handleWidgetDelete = (widgetId) => {
    const widgets = this.props.widgets.map((widget) => {
      if (widget && widget.id === widgetId) {
        return null;
      }

      return widget;
    });

    this.props.onWidgetChange(this.props.id, widgets);
  };

  handleWidgetDrop = (subsectionIndex, newWidget) => {
    const { details: { subsections } } = this.getSection();
    const { type } = this.props;
    const widgets = subsections.map((subsection, index) => {
      if (index === subsectionIndex && type !== 'AddOns') {
        const widgetWithId = { ...newWidget, id: uuid.v4() };
        widgetResolver(this.context, widgetWithId);
        return widgetWithId;
      }

      return this.props.widgets[index];
    });

    this.props.onWidgetChange(this.props.id, widgets);
  };

  renderSubsection = (subsection, index) => {
    const {
      widgets,
      isWidgetBeingEdited,
      onWidgetEditStart,
      onWidgetEditStop,
      onStartWidgetSwitch,
      onSwitchWidgets,
      sectionWidgetMoving,
      pageFacilityId,
      locale,
      isSubmitting,
      legacyWidgetLoader,
    } = this.props;
    const widget = widgets[index];

    let props = {
      isEditing: false,
      accepts: subsection.accepts,
      onDrop: this.handleWidgetDrop,
      position: index,
      pageFacilityId,
      locale,
    };
    if (widget) {
      props = {
        ...props,
        isEditing: isWidgetBeingEdited(widget.id),
        onWidgetEditStart,
        onFinishEdit: this.handleFinishEditSubsection,
        onCancelEdit: onWidgetEditStop,
        onDelete: this.handleWidgetDelete,
        onStartWidgetSwitch,
        onSwitchWidgets,
        sectionWidgetMoving,
        locale,
        isSubmitting,
        legacyWidgetLoader,
      };
    }

    return (
      <Subsection
        key={index}
        widget={widget}
        {...props}
      />
    );
  };

  render() {
    const section = this.getSection();
    const {
      isEditing,
      id,
      data,
      payload,
      type,
      sectionWidgetMoving,
    } = this.props;

    const subsections = section.details.subsections.map(this.renderSubsection);
    const Component = section;
    const { authorization } = section;

    // Sections can only be replaced by others of the same region -- is this a faulty assumption?
    const accepts = [this.getSection().details.region];

    const classes = [
      style.root,
      type === 'ChildBlock' && style.pageContent,
      type.includes('Footer') && style.footer,
    ];

    const componentProps = {
      id,
      ...(section.details.defaultPayload || {}),
      ...data,
      ...payload,
      type,
      isEditable: true,
      isBuilderView: true,
      isEditing,
      onDelete: this.handleDelete,
      onEditStart: this.handleEditStart,
      onFinishEdit: this.handleFinishEdit,
      onCancelEdit: this.handleCancelEdit,
      onSwapUp: this.handleSwapUp,
      onSwapDown: this.handleSwapDown,
      showSwapArrows: this.handleShowSwapArrows,
      sectionWidgetMoving,
      pageFacilityId: this.props.pageFacilityId,
    };


    let component;
    if (authorization) {
      component = (
        <Authorization {...authorization}>
          {authorized => (
            <Component
              {...componentProps}
              unauthorized={!authorized}
            >
              {subsections}
            </Component>
          )}
        </Authorization>
      );
    } else {
      component = (
        <Component {...componentProps}>
          {subsections}
        </Component>
      );
    }

    return (
      <div className={cx(classes)}>
        <SectionDropZone onDrop={this.handleDrop} onCanDrop={this.handleCanDrop} accepts={accepts}>
          {component}
        </SectionDropZone>
      </div>
    );
  }
}
